import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  TextField,
  CircularProgress,
  SxProps,
  Button,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import UserAnalyticsTable from './UserAnalyticsTable';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { SearchOutlined } from '@mui/icons-material';
import {
  getMessageRecipientsStats,
  getMessageReplyRecipientsStats,
} from '../api/requests/users';
import { GridColDef } from '@mui/x-data-grid';
import { formatFullDateTime } from '../utils/utils';
import { sectionDescriptionSx, sectionTitleSx } from './UserAnalytics';
import { colors } from '../utils/constants';
import ClearIcon from '@mui/icons-material/Clear';

interface RowsType {
  id: number;
  viewed: string;
  rank: string;
  name: string;
  repliedOn?: string;
}

interface RecipientType {
  isAdmin: boolean;
  rank: string;
  userId: number;
  userName: string;
  viewedOn: string;
  repliedOn?: string;
}
interface ViewRecipientsDialogProps {
  open: boolean;
  handleClose: () => void;
  id: number;
  title: string;
  description: string;
  section?: string;
}

const ViewRecipientsDialog = ({
  open,
  handleClose,
  id,
  title,
  description,
  section,
}: ViewRecipientsDialogProps) => {
  const [allRows, setAllRows] = useState<RowsType[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [filteredRows, setFilteredRows] = useState<RowsType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    setSearchText(searchValue);
    if (!searchValue) {
      setFilteredRows(allRows);
      return;
    }
    const filtered = allRows.filter((row) =>
      Object.values(row).some(
        (val) => val && val.toString().toLowerCase().includes(searchValue)
      )
    );
    setFilteredRows(filtered);
  };

  const renderProgress = (
    value: string | undefined,
    IconComponent: React.ElementType,
    fallbackText: string
  ) => {
    return (
      <Box display='flex' alignItems='center' gap={1}>
        {value ? (
          <>
            <IconComponent sx={{ fontSize: 16 }} />
            <Typography fontSize='13px'>{formatFullDateTime(value)}</Typography>
          </>
        ) : (
          <>
            <CloseIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
            <Typography fontSize='13px' color='#9e9e9e'>
              {fallbackText}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      renderCell: (params) => (
        <Typography fontWeight={400} fontSize='12px' color='#2f2f2f'>
          {params.value || '--'}
        </Typography>
      ),
    },

    {
      field: 'rank',
      headerName: 'Rank',
      flex: 1,
      renderCell: (params) => (
        <Typography fontWeight={400} fontSize='12px' color='#2f2f2f'>
          {params.value || '--'}
        </Typography>
      ),
    },
    {
      field: 'viewed',
      headerName: 'Viewed On',
      flex: 1,
      renderCell: (params) =>
        renderProgress(params.value, RemoveRedEyeOutlinedIcon, 'Not viewed'),
    },
    ...(section === 'Mandatory Replies'
      ? [
          {
            field: 'repliedOn',
            headerName: 'Replied On',
            flex: 1,
            renderCell: (params: any) =>
              renderProgress(params.value, ReplyOutlinedIcon, 'Not Replied'),
          },
        ]
      : []),
  ];

  const fetchForwardRecipients = async () => {
    setLoading(true);
    try {
      const response =
        section === 'Mandatory Replies'
          ? await getMessageReplyRecipientsStats({ messageId: id })
          : await getMessageRecipientsStats({ messageId: id });
      if (response) {
        const updatedResponse = response.map((item: RecipientType) => ({
          id: item.userId,
          viewed: item.viewedOn,
          rank: item.rank,
          name: item.userName,
          repliedOn: item.repliedOn,
        }));
        setAllRows(updatedResponse);
        setFilteredRows(updatedResponse);
      }
    } catch (error) {
      console.error('Failed to fetch forward recipients', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForwardRecipients();
  }, [id]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0,
        }}
      >
        <Box>
          <Typography sx={sectionTitleSx}>{title}</Typography>
          <Typography sx={sectionDescriptionSx}>{description}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          variant='outlined'
          size='small'
          placeholder={`Search Recipients...`}
          value={searchText}
          onChange={handleSearch}
          slotProps={{
            input: {
              sx: { fontSize: '12px', height: '45px', borderRadius: '8px' },
              endAdornment: (
                <>
                  {searchText && (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setSearchText('')}
                        size='small'
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )}
                  <Divider
                    sx={{ color: 'red', ml: 1, mr: 2 }}
                    orientation='vertical'
                  />
                  <SearchOutlined sx={{ mr: 1, color: 'gray' }} />
                </>
              ),
            },
          }}
          sx={{ width: '400px', mb: 2 }}
        />
        {loading ? (
          <Box sx={loaderSx}>
            <CircularProgress size={60} thickness={6} />
          </Box>
        ) : filteredRows.length > 0 ? (
          <UserAnalyticsTable
            columns={columns}
            rows={filteredRows}
            sx={{ height: '100%', width: '100%' }}
          />
        ) : (
          <Box sx={loaderSx}>
            <Typography variant='body2' color='textSecondary'>
              No data found.
            </Typography>
          </Box>
        )}
        <Box sx={closeButtonBoxStyles}>
          <Button sx={cancelButtonStyles} onClick={handleClose}>
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ViewRecipientsDialog;

export const loaderSx: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '40px',
};

const cancelButtonStyles: SxProps = {
  fontSize: 14,
  color: colors.indigoBlueColor,
  border: `1px solid ${colors.indigoBlueColor}`,
  cursor: 'pointer',
  textTransform: 'inherit',
  padding: '2px 16px',
  borderRadius: '8px',
  fontWeight: 400,
};

const closeButtonBoxStyles: SxProps = {
  justifyContent: 'flex-end',
  display: 'flex',
  mt: 2,
};
